import { TrackingUtils } from '../../js/trackings.utils';
import { SupportPayload } from './../../js/product/product.models';
import { envConfig } from './../../js/config/envconfig';
import { AnswerClicked } from "./survey.handlers";
import surveyJson from '../../mocks/survey.json';
import {
  fetchAnswerWidget,
  fetchZipCodeCheck,
  fetchStepBody,
  fetchBottomNavigationPanel,
  fetchZipCodeNotAvailable,
  fetchLeadTakenBox,
} from "./survey.renderers";

import { SurveyUtils } from "./survey-utils";

import {
  SurveyStep,
  SurveyState,
  ActionListeners,
  ListenerRecord,
  StepType,
  EventType,
  AnalyticsSupportTypeName,
  SurveySupportType,
} from "./survey-models";

import {
  ANSWERS_SELECTOR,
  ANSWER_BUTTON_ID_PREFIX,
  BACK_BUTTON_SELECTOR,
  FORWARD_BUTTON_SELECTOR,
  HASH_SEPARATOR,
  NAVIGATION_PANEL_SELECTOR,
  QUESTION_SELECTOR,
  SUBTITLE_SELECTOR,
  SURVEY_BODY_SELECTOR,
  SURVEY_PROGRESSBAR,
  SURVEY_PROGRESSBAR_FILL,
  ZIPCODE_CHECK_BUTTON_SELECTOR,
  ZIPCODE_CHECK_INPUT_SELECTOR,
  ZIPCODE_EMAIL_BUTTON_SELECTOR,
  ZIPCODE_EMAIL_INPUT_SELECTOR,
  ZIPCODE_ERROR_LABEL_SELECTOR,
  ZIPCODE_ERROR_NO_PRIVACY_LABEL_SELECTOR,
  ZIPCODE_LEAD_BOX_SELECTOR,
  ZIPCODE_PRIVACY_INPUT_SELECTOR,
  ZIP_CODE_FOUND,
  ZIP_CODE_NOT_FOUND,
  ZIP_CODE_PRIVACY_HTML_SELECTOR,
} from "./survey.constants";
import Utils from "../../js/utils";
import Validators from '../../js/validators';

declare global {
  interface Window {
    survey_json: SurveyStep;
    survey_support_url: URL;
  }
}
export class Survey {
  private surveyState: SurveyState;
  private stateCache: SurveyState; //cache per gestire il forward
  private readonly tracking: TrackingUtils;
  private root: SurveyStep;
  private answersEl: HTMLElement | undefined; //container card risposte
  private questionEl: HTMLElement | undefined; //container titolo della domanda
  private subTitleEl: HTMLElement | undefined; //container sotto titolo (name dello step root)
  private navigationPanelEl: HTMLElement | undefined; //container pannello con button back/forward
  private readonly actionListeners: ActionListeners = new Map<
    HTMLElement,
    ListenerRecord
  >(); //tengo traccia dei listeners attivi in modo da poterli deregistrare in batch senza scrivere codice specifico
  /**
   * @param  {HTMLElement}  elemento di partenza in cui verrà renderizzato il questionari;o
   */
  constructor(private readonly rootEl: HTMLElement) {
    this.loadSurvey();
    this.tracking = new TrackingUtils();
  }

  /**
   * @description metodo di partenza del questionario
   * @returns {Promise<void>}
   */
  private async loadSurvey(): Promise<void> {
    if (this.rootEl) {
      this.getData().then((res) => {
        this.root = <SurveyStep>res;
        console.log(
          "%cData successfully fetched",
          "color: midnightblue; background-color: darkseagreen; padding: 2px; border-radius: 3px;"
        );
        //ottengo lo stato del questionario in base all'url + fragments
        SurveyUtils.getStepHistory(this.root, this.stateCache).then(
          async (history: { state: SurveyState; cache: SurveyState }) => {
            this.surveyState = history.state;
            this.stateCache = history.cache;

            //recupero l'ultimo step
            let firstStep: SurveyStep = null;
            if (this.surveyState.paths.length == 0) {
              firstStep = this.root; //nessuno stato, carico il primo step
            } else { //risalgo l'albero partendo dalla root
              firstStep = await SurveyUtils.GetQuestionByPath(this.surveyState.paths, this.root);
            }
            //renderizzo il contenitore in cui vengono renderizzati i componenti del sondaggio
            await SurveyUtils.render(this.rootEl, await fetchStepBody());
            //inizializzo i selector delle azioni
            await this.initBodySelectors();
            //carico il primo step (root)
            await this.loadStep(firstStep);
            //inizializzo il routing
            await this.initHashChangeHandler.call(this);
          }
        );
      });
    } else {
      throw new Error(
        "%cImpossibile renderizzare il questionario se l'elemento di root è null o undefined:"
      );
    }
  }

  /**
   * @param  {SurveyStep} step step del questionario (domanda e risposte) da renderizzare
   * @returns {Promise<string>} html di tutte le risposte collegate allo step / domanda
   */
  private async buildAnswers(step: SurveyStep): Promise<string> {
    console.log(
      `%cRendering Step ${this.root.name}...`,
      "color: midnightblue; background-color: darkseagreen; padding: 2px; border-radius: 3px;"
    );

    console.log(
      `%cQuestion ${this.root.question}...`,
      "color: midnightblue; background-color: darkseagreen; padding: 2px; border-radius: 3px;"
    );
    const answers = await SurveyUtils.FetchAnswers(step);
    const cards: string[] = [];
    const lastAnswer =
      this.surveyState.paths.at(this.surveyState.paths.length - 1)
        ?.lastAnswer ?? this.surveyState.rootAnswer;
    //ottengo la card per ogni risposta
    for (let i = 0; i < answers.length; i++) {
      cards.push(await fetchAnswerWidget(answers[i], answers[i] == lastAnswer));
    }
    return cards.reduce((curr, next) => curr + next);
  }
  /**
   * @description renderizza la card della domanda passata
   * @param  {string} answer domanda da renderizzare
   * @return {Promise<void>}
   */
  private async renderAnswers(step: SurveyStep): Promise<void> {
    const answers = await this.buildAnswers(step);
    return SurveyUtils.render(this.answersEl, answers);
  }
  private async renderZipCodeCheck(idMestiere: string): Promise<void> {
    this.rootEl.dataset.idmestiere = idMestiere;
    await SurveyUtils.render(
      this.answersEl,
      await fetchZipCodeCheck(idMestiere, this.surveyState.insertedCap)
    );
  }
  private async renderLeadTaken() {
    const leadBox = this.rootEl.querySelector<HTMLDivElement>('#' + ZIPCODE_LEAD_BOX_SELECTOR);
    await SurveyUtils.render(leadBox, await fetchLeadTakenBox())
  }
  /**
   * @description renderizza il pannello sotto le domande per back / forward
   * @return {Promise<void>}
   */
  private async renderNavigationPanel(
    back: boolean = false,
    forward: boolean = false
  ): Promise<void> {
    const navHtml = await fetchBottomNavigationPanel(back, forward);
    await SurveyUtils.render(this.navigationPanelEl, navHtml);
    const bodyEl = this.rootEl.querySelectorAll<HTMLElement>(
      "#" + SURVEY_BODY_SELECTOR
    )[0];; //voglio il body del questionario, in quanto l'attivazione dello sticky si basa su quell'elemento che va in overflow
    Utils.OverflowObserver(this.navigationPanelEl, 'sticky');
  }
  /**
   * @param  {string} questionText domanda da renderizzare
   * @returns {Promise<void>} renderizza il testo della domanda passata
   */
  private async renderQuestionText(questionText: string): Promise<void> {
    SurveyUtils.render(this.questionEl, questionText);
  }
  /**
   * @description inizializzo tutti gli handler per le card
   * @param  {SurveyStep} step step corrente
   * @returns {Promise<void>}
   */
  private async initAnswersHandlers(step: SurveyStep): Promise<void> {
    //registro gli handler delle card con le risposte
    step.children.forEach(async (child) => {
      const childElement = this.answersEl.querySelector<HTMLElement>(
        "#" +
        ANSWER_BUTTON_ID_PREFIX +
        "-" +
        SurveyUtils.sanitizeText(child.name)
      );
      SurveyUtils.registerHandler(
        childElement,
        {
          handler: AnswerClicked.bind(this, child, this.surveyState),
          eventType: EventType.CLICK,
        },
        this.actionListeners
      );
    });
  }
  private async initZipCodeHandlers() {
    const buttonEl = this.rootEl.querySelectorAll<HTMLButtonElement>(
      "#" + ZIPCODE_CHECK_BUTTON_SELECTOR
    )[0];
    const inputEl: HTMLInputElement =
      this.rootEl.querySelectorAll<HTMLInputElement>(
        "#" + ZIPCODE_CHECK_INPUT_SELECTOR
      )[0];
    const errorEl: HTMLInputElement =
      this.rootEl.querySelectorAll<HTMLInputElement>(
        "#" + ZIPCODE_ERROR_LABEL_SELECTOR
      )[0];
    if (!this.surveyState.insertedCap) {
      Utils.enableSubmitButton(buttonEl, false);
    }
    const buttonClickHandler = async (event: Event) => {
      event.preventDefault();
      Utils.toggleSubmitButtonStatus(buttonEl, "loading");
      inputEl.classList.remove("invalid");
      const zipcode = inputEl?.value?.trim();
      const idMestiere = inputEl?.dataset.idmestiere;
      const result = await SurveyUtils.checkZipcode(zipcode, idMestiere);
      if (!result) { //errore api, riattivo il botttone
        Utils.enableSubmitButton(buttonEl, true);
        return;
      }
      if (result.success == true) {
        const currentStep = SurveyUtils.GetQuestionByPath(
          this.surveyState.paths,
          this.root
        );
        const nextStep = (await currentStep).children.find(
          (res) => res.name?.toLowerCase() == ZIP_CODE_FOUND.toLowerCase()
        );
        SurveyUtils.addZipCodeQueryString(zipcode?.toString());
        //mando
        window.location.hash = [
          ...this.surveyState.paths.map((res) => encodeURIComponent(res.name)),
          encodeURIComponent(nextStep.name),
        ].join(HASH_SEPARATOR);
      } else if (!result.errorMessage) {
        const currentStep = SurveyUtils.GetQuestionByPath(
          this.surveyState.paths,
          this.root
        );
        const nextStep = (await currentStep).children.find(
          (res) => res.name?.toLowerCase() == ZIP_CODE_NOT_FOUND.toLowerCase()
        );

        await SurveyUtils.addZipCodeQueryString(zipcode?.toString());

        window.location.hash = [
          ...this.surveyState.paths.map((res) => encodeURIComponent(res.name)),
          encodeURIComponent(nextStep.name),
        ].join(HASH_SEPARATOR);
      } else {
        inputEl.classList.add("invalid");
        Utils.enableSubmitButton(buttonEl, true);
        SurveyUtils.render(errorEl, result.errorMessage);
      }
    };

    const inputHandler = async (event: KeyboardEvent) => {
      if (event.code !== "Enter") {
        const target = event.target as HTMLInputElement;
        const cap = target.value?.trim().toString();

        if (Validators.validateCap(cap)) {
          Utils.enableSubmitButton(buttonEl, true);
        } else {
          Utils.enableSubmitButton(buttonEl, false);
        }
      }
    };

    //registro gli handlers
    SurveyUtils.registerHandler(
      buttonEl,
      {
        handler: buttonClickHandler,
        eventType: EventType.CLICK,
      },
      this.actionListeners
    );
    SurveyUtils.registerHandler(
      inputEl,
      { handler: inputHandler, eventType: EventType.KEYUP },
      this.actionListeners
    );
  }
  /**
   * @description inizializza il listener e l'handler per il cambio di url (hash)
   */
  private async initHashChangeHandler() {
    window.addEventListener("popstate", async (event: Event) => {
      //update state
      const history = await SurveyUtils.getStepHistory(
        this.root,
        this.stateCache
      );
      this.surveyState = history.state;
      this.stateCache = history.cache;
      
      //ottengo l'ultima risposta data
      const lastAnswer: string | undefined = this.surveyState.paths.at(
        this.surveyState.paths.length - 1
      )?.name;
      await this.loadStep(
        await SurveyUtils.GetQuestionByPath(this.surveyState.paths, this.root)
      );
      event.preventDefault();
    });
  }
  private async iniNavigationButtonHandlers() {
    const backEl = this.rootEl.querySelectorAll<HTMLElement>(
      "#" + BACK_BUTTON_SELECTOR
    )[0];
    const forwardEl = this.rootEl.querySelectorAll<HTMLElement>(
      "#" + FORWARD_BUTTON_SELECTOR
    )[0];
    const backHandlerDef = async () => {
      if (this.surveyState.paths.length == 0) {
        return;
      }
      // this.stateCache = JSON.parse(JSON.stringify(this.surveyState))

      const paths = await SurveyUtils.GetUrlPath();
      paths.pop(); //rimuovo l'ultimo path
      if (paths.length > 0) {
        window.location.hash = paths.map(encodeURIComponent).join(HASH_SEPARATOR);
      } else {
        window.location.hash = "";
      }
    };
    const forwardHandlerDef = async () => {
      const paths = await SurveyUtils.GetUrlPath();
      paths.push(
        this.surveyState.currentStep.lastAnswer ?? this.surveyState.rootAnswer
      );
      if (paths.length != 0 && paths[0]) {
        window.location.hash = paths.map(encodeURIComponent).join(HASH_SEPARATOR);
      } else {
        window.location.hash = encodeURIComponent(this.surveyState.rootAnswer);
      }
    };
    SurveyUtils.registerHandler(
      backEl,
      { handler: backHandlerDef, eventType: EventType.CLICK },
      this.actionListeners
    );
    SurveyUtils.registerHandler(
      forwardEl,
      { handler: forwardHandlerDef, eventType: EventType.CLICK },
      this.actionListeners
    );
  }
  /**
   * @description pulisco tutti i listener
   * @returns {Promise<void>}
   */
  private async clearActionHandlers(): Promise<void> {
    this.actionListeners.forEach(
      async (value: ListenerRecord, element: HTMLElement) => {
        SurveyUtils.deregisterHandler(element, value, this.actionListeners);
      }
    );
  }
  /**
   * @description carimento dello step selezionato, con logica per decidere cosa e come renderizzare il tutto, a seconda della configurazione dello step selezionato
   * @param  {SurveyStep} step step da renderizzare
   * @returns {Promise<void>}
   */
  private async loadStep(step: SurveyStep): Promise<void> {
    console.log(
      `%cRendering survey question ${step?.question}...`,
      "color: midnightblue; background-color: darkseagreen; padding: 2px; border-radius: 3px;"
    );
    const idMestiere = this.rootEl.dataset.idmestiere ?? '';

    window.scroll({
      left: 0,
      top: 0,
      behavior: "smooth"
    })
    const completed = SurveyUtils.CalcProgressBar(
      this.surveyState,
      this.root,
      step
    );
    const progressBarEl = document.querySelector<HTMLElement>( //oggetto progressbar renderizzato nell'header.php
      "#" + SURVEY_PROGRESSBAR_FILL
    );
    //aggiorno la progress bar
    progressBarEl.style.width = `${completed}%`;
    //pulisco gli handlers prima di eliminare i nodi per evitare conflitti (sopratutto nel bottone previous che di fatto ha sempre lo stesso id)
    await this.clearActionHandlers();
    if (!step.type) {
      //se lo step non è cap not found
      if (step.name?.toLowerCase() == ZIP_CODE_NOT_FOUND.toLowerCase()) {
        await SurveyUtils.render(
          this.answersEl,
          await fetchZipCodeNotAvailable(this.surveyState.insertedCap)
        ).then(async _ => {
          await this.tracking.trackSurveyUnavailableCoverage(this.surveyState, this.surveyState.insertedCap, idMestiere ?? step.question ?? step.name);
        })

        const privacyTextEl = this.rootEl.querySelector('#' + ZIP_CODE_PRIVACY_HTML_SELECTOR);
        privacyTextEl.innerHTML = php_pars.privacy_text_html;
        //gestione raccolta dati assistenza
        const sendEmailEL = this.rootEl.querySelector<HTMLButtonElement>('#' + ZIPCODE_EMAIL_BUTTON_SELECTOR);
        const emailInput = this.rootEl.querySelector<HTMLButtonElement>('#' + ZIPCODE_EMAIL_INPUT_SELECTOR);
        const privacyEl = this.rootEl.querySelector<HTMLInputElement>('#' + ZIPCODE_PRIVACY_INPUT_SELECTOR);
        const privacyErrorEl = this.rootEl.querySelector<HTMLSpanElement>('#' + ZIPCODE_ERROR_NO_PRIVACY_LABEL_SELECTOR)

        let handler = (event: Event) => {
          event.preventDefault();
          privacyErrorEl.classList.add('d-none');
          privacyEl.classList.remove('invalid');

          if (event.type == EventType.KEYUP) {
            if ((event as KeyboardEvent).code !== "Enter") {
              return;
            }
          }
          const mailCheck = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g;
          if (!mailCheck.test(emailInput.value)) {
            console.error('Mail non valida')
            return;
          }
          if (!privacyEl.checked) {
            privacyErrorEl.classList.remove('d-none');
            privacyEl.classList.add('invalid');
            return;
          }
          const email = emailInput?.value;
          SurveyUtils.doSupportCall(this.surveyState?.insertedCap, email).then((res) => {
            if (res) {
              console.log(res);
              this.renderLeadTaken();
            } else {
              console.error('Api error');
            }
          })
        }
        SurveyUtils.registerHandler(sendEmailEL, { handler: handler, eventType: EventType.CLICK }, this.actionListeners)
        SurveyUtils.registerHandler(emailInput, { handler: handler, eventType: EventType.KEYUP }, this.actionListeners)
      } else {
        //renderizzo le card delle risposte

        await this.renderAnswers(step);
        await this.initAnswersHandlers(step);
      }
    } else {
      switch (
      step.type //decido cosa renderizzare in base al tipo (StepType)
      ) {
        case StepType.ANSWERS:
          //renderizzo le card delle risposte
          await this.renderAnswers(step);
          await this.initAnswersHandlers(step);
          break;
        case StepType.ZIPCODE:
          //rendrizzo il check del cap
          await this.renderZipCodeCheck(step.idMestiere ?? 'IdMestiere_missing').then(() => {
            if (this.surveyState.insertedCap) {
              this.rootEl.querySelector<HTMLButtonElement>(
                "#" + ZIPCODE_CHECK_BUTTON_SELECTOR
              ).disabled = false;
            }
          });
          await this.initZipCodeHandlers();
          break;
        case StepType.SUPPORT:
          console.log(
            `%cRedirect a supporto...`,
            "color: darkslategrey; background-color: darkorange; padding: 2px; border-radius: 3px;"
          );
          break;
        default:
          throw new Error(`Step type ${step.type} non riconosciuto`);
      }
    }
    if (this.surveyState.paths.length > 0) {
      //se lo stato ha dei path significa che non siamo all'inizio del questionario e serve il bottone indietro
      SurveyUtils.render(this.subTitleEl, "");
      await this.renderNavigationPanel(true, this.surveyState.forward); //navigation panel con button attivi (forward calcolato dal getstephistory)
      this.iniNavigationButtonHandlers();
    } else {
      //in caso di primo step no previous button
      SurveyUtils.render(this.subTitleEl, step.name);
      await this.renderNavigationPanel(false, this.surveyState.forward); //navigation panel senza buttons attivi
      this.iniNavigationButtonHandlers();
    }
    if (step.name?.toLowerCase() != ZIP_CODE_NOT_FOUND.toLowerCase()) {
      //tracking step caricato
      this.tracking.trackSurveyStep(this.surveyState, step, idMestiere);
    }
    if (step.name && !step.url && !step.type) {
      if (!step.question) {
        step.question = step.name;
      }
    }
    this.renderQuestionText(step.question);
  }

  /**
   * @description ottengo il primo step parsando il json TODO definire dove metterlo e come ottenerlo
   * @returns {Promise<SurveyStep>} step root da cui parte il questionario
   */
  private async getData(): Promise<SurveyStep> {
    console.log(
      "%cFetching survey configuration...",
      "color: midnightblue; background-color: darkseagreen; padding: 2px; border-radius: 3px;"
    );
    if (envConfig.globalMockEnabled) {
      return surveyJson as SurveyStep;
    } else {
      if (window.survey_json)
        return window.survey_json as SurveyStep;
      else if (envConfig.fallbackMock) {
        return surveyJson as SurveyStep;
      } else {
        throw new Error("Errore caricamento Questionario: nessun json configurato");
      }

    }
  }

  /**
   * @description ottengo gli htmlelements principali dopo aver renderizzato il body, partendo dai vari selettori, in modo da facilitare le query e la modifica dei nodi
   * @returns {Promise<void>}
   */
  private async initBodySelectors(): Promise<void> {
    if (!this.rootEl) {
      console.error(
        "%cError initializing survey...",
        "color: red; background-color: darkseagreen; padding: 2px; border-radius: 3px;"
      );
      return;
    }
    this.answersEl = this.rootEl.querySelectorAll<HTMLElement>(
      "#" + ANSWERS_SELECTOR
    )[0];

    this.questionEl = this.rootEl.querySelectorAll<HTMLElement>(
      "#" + QUESTION_SELECTOR
    )[0];

    this.subTitleEl = this.rootEl.querySelectorAll<HTMLElement>(
      "#" + SUBTITLE_SELECTOR
    )[0];
    this.navigationPanelEl = this.rootEl.querySelectorAll<HTMLElement>(
      "#" + NAVIGATION_PANEL_SELECTOR
    )[0];
    return;
  }
}
